import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  ColorTestPilot,
  IgButton,
  IgTextFieldTopLabel,
} from "@intelligems/ig-theme";
import { Checkbox, FormControlLabel, Grid } from "@mui/material";

import { useExperienceAnalysis } from "../../../../../api/ai/experience-analysis";
import { useLazyUpdateValue } from "../../../../../hooks/util";
import {
  dispatchExperienceAnalysis,
  experienceAnalysis,
} from "../../../../../store/slices/ai";
import { mpClickAiExperienceAskAbieButton } from "../../../../../utils/mixpanel";
import { AiDisclaimer } from "../Disclaimer";

const questions = [
  "Which test group was the best performer?",
  "Which test group was the worst performer?",
  "Which test group should I kill?",
  "Which test group should I roll out?",
  "Should I keep this test running longer?",
];

export const AskQuestionRow = () => {
  const analysis = useSelector(experienceAnalysis);
  const dispatch = useDispatch();
  const [question, setQuestion] = useImmer<string>(analysis.query || "");
  const getExperienceAnalysis = useExperienceAnalysis();

  useEffect(() => {
    setQuestion(analysis.query || "");
  }, [analysis.query]);

  useLazyUpdateValue(question, (value) => {
    dispatch(
      dispatchExperienceAnalysis({
        type: "setQuestion",
        value,
      })
    );
  });

  const onClick = () => {
    dispatch(
      dispatchExperienceAnalysis({
        type: "sendQuery",
        value: question,
      })
    );
    if (analysis.experience && analysis.queryTemplate && analysis.threadId) {
      getExperienceAnalysis.mutate({
        body: {
          assistantId: analysis.assistantId,
          experienceId: analysis.experience.id!,
          queryTemplate: analysis.queryTemplate,
          threadId: analysis.threadId,
          query: analysis.query,
        },
        pathParams: undefined,
        queryParams: undefined,
      });
    }
    mpClickAiExperienceAskAbieButton(question);
  };

  return (
    <Grid
      container
      item
      direction={"column"}
      spacing={2}
      sx={{ paddingTop: "10px" }}
    >
      <Grid
        item
        container
        alignItems={"center"}
        columnSpacing={2}
        flexWrap={"nowrap"}
      >
        <Grid item xs={11}>
          <IgTextFieldTopLabel
            sx={{
              "&.Mui-focused": {
                color: ColorTestPilot.blue,
              },
            }}
            testPilotBlue={true}
            disabled={!analysis.isReadyForQuery}
            label={"Ask Abie"}
            fullWidth={true}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
        </Grid>
        <Grid item sx={{ marginTop: "23px", textAlign: "end" }}>
          <IgButton
            igVariant={"testPilotBlue"}
            sx={{ minWidth: "90px" }}
            loading={!analysis.isReadyForQuery}
            onClick={onClick}
          >
            Ask Abie
          </IgButton>
        </Grid>
      </Grid>
      <Grid item sx={{ paddingTop: "0px !important" }}>
        <Grid
          container
          item
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Grid item sx={{ marginLeft: "auto", marginRight: "auto" }}>
            <AiDisclaimer show={analysis.isMessageComplete} />
          </Grid>
          <Grid item>
            <FormControlLabel
              sx={{
                marginRight: "0px",
                [`& .Mui-checked`]: {
                  color: `${ColorTestPilot.blue} !important`,
                },
              }}
              label={"Auto Scroll"}
              control={
                <Checkbox
                  size={"small"}
                  checked={analysis.autoScroll}
                  onClick={(e) => {
                    dispatch(
                      dispatchExperienceAnalysis({
                        type: "setAutoScroll",
                        // @ts-expect-error
                        value: e.target.checked,
                      })
                    );
                  }}
                />
              }
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item sx={{ marginRight: "114px" }}>
        <Grid container item spacing={1} justifyContent={"center"}>
          {questions.map((question) => (
            <Grid item key={question}>
              <IgButton
                disabled={!analysis.isReadyForQuery}
                igVariant={"testPilotBlueOutlined"}
                onClick={() => setQuestion(question)}
              >
                {question}
              </IgButton>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};
